import { eventChannel } from 'redux-saga';
import { call, put, select, take, takeEvery } from 'redux-saga/effects';

import { configLoadDone } from '@pressreader/config';
import { error } from '@pressreader/logger';
import { getBasePath, getCurrentPath, withAbsolutePath } from '@pressreader/routing';
import { cookies } from '@pressreader/utils';

import {
    COOKIE_FACELIFT_ENABLED_EXPIRES_IN_DAYS,
    COOKIE_FACELIFT_ENABLED_KEY,
    COOKIE_TARGETPLATFORM_EXPIRES_IN_DAYS,
    COOKIE_TARGETPLATFORM_KEY,
} from '../constants';
import { OnlineStatus } from '../types/online.status';
import { resolveFaceliftEnabled } from '../utils';

import {
    appStarted,
    changeTargetPlatformAction,
    setOnlineStatus,
    startAppAction,
    toggleFaceliftUiAction,
    updateCoreModuleStateAction,
    watchOnlineStatus,
} from './actions';
import { selectAppStarted, selectDefaultTargetPlatform, selectFaceliftEnabled, selectSupportedPlatforms, selectTargetPlatform } from './selectors';

function* startApp() {
    yield put(watchOnlineStatus());

    const appAlreadyStarted: ReturnType<typeof selectAppStarted> = yield select(selectAppStarted);
    if (appAlreadyStarted) {
        return;
    }
    yield put(appStarted());
}

function* changeTargetPlatform(action: ReturnType<typeof changeTargetPlatformAction>) {
    const { newTargetPlatform, keepLocation } = action.payload;

    const defaultTargetPlatform: ReturnType<typeof selectTargetPlatform> = yield select(selectDefaultTargetPlatform);
    const supportedPlatforms: ReturnType<typeof selectSupportedPlatforms> = yield select(selectSupportedPlatforms);

    if (supportedPlatforms.indexOf(newTargetPlatform) < 0) {
        return;
    }

    if (newTargetPlatform === defaultTargetPlatform) {
        cookies.set(COOKIE_TARGETPLATFORM_KEY, '', {
            path: getBasePath(),
            expiresInDays: -1,
        });
    } else {
        cookies.set(COOKIE_TARGETPLATFORM_KEY, newTargetPlatform, {
            path: getBasePath(),
            expiresInDays: COOKIE_TARGETPLATFORM_EXPIRES_IN_DAYS,
        });
    }

    if (keepLocation) {
        window.location.assign(getCurrentPath());
    } else {
        // reload the page
        window.location.assign(withAbsolutePath(''));
    }
}

function* toggleFaceliftUi(action: ReturnType<typeof toggleFaceliftUiAction>) {
    const { enable } = action.payload;

    const enabled: ReturnType<typeof selectFaceliftEnabled> = yield select(selectFaceliftEnabled);

    if (enabled === enable) {
        return;
    }

    if (!enable) {
        cookies.set(COOKIE_FACELIFT_ENABLED_KEY, '', {
            path: getBasePath(),
            expiresInDays: -1,
        });
    } else {
        cookies.set(COOKIE_FACELIFT_ENABLED_KEY, 'true', {
            path: getBasePath(),
            expiresInDays: COOKIE_FACELIFT_ENABLED_EXPIRES_IN_DAYS,
        });
    }

    // reload the page
    window.location.assign(withAbsolutePath(''));
}

function* setUpFaceLiftOnConfigLoad() {
    const isFaceliftEnabled: ReturnType<typeof selectFaceliftEnabled> = yield select(selectFaceliftEnabled);
    const shouldFaceliftBeEnabled = resolveFaceliftEnabled();
    const appStarted: ReturnType<typeof selectAppStarted> = yield select(selectAppStarted);

    if (!appStarted || isFaceliftEnabled === shouldFaceliftBeEnabled) {
        return;
    }

    yield put(
        updateCoreModuleStateAction({
            faceliftEnabled: isFaceliftEnabled,
        }),
    );
    // reload the page
    window.location.assign(withAbsolutePath(''));
}

function createOnlineStatusChannel() {
    return eventChannel<OnlineStatus>(emitter => {
        const handleOnline = () => {
            emitter({ isOnline: true });
        };

        const handleOffline = () => {
            emitter({ isOnline: false });
        };

        // TODO: refactor using poll https://dev.azure.com/pressreader/PressReader/_workitems/edit/170457
        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    });
}

function* watchOnlineStatusSaga() {
    yield put(setOnlineStatus({ isOnline: navigator.onLine }));

    const channel: ReturnType<typeof createOnlineStatusChannel> = yield call(createOnlineStatusChannel);

    try {
        while (true) {
            const { isOnline }: OnlineStatus = yield take(channel);
            yield put(setOnlineStatus({ isOnline }));
        }
    } catch (err) {
        error(err);
    } finally {
        channel.close();
    }
}

function* saga() {
    yield takeEvery(startAppAction, startApp);
    yield takeEvery(changeTargetPlatformAction, changeTargetPlatform);
    yield takeEvery(toggleFaceliftUiAction, toggleFaceliftUi);

    yield takeEvery(configLoadDone, setUpFaceLiftOnConfigLoad);
    yield takeEvery(watchOnlineStatus, watchOnlineStatusSaga);
}
export { saga };
